<script lang="ts">
    import Vue from "vue";
    import Component from 'vue-class-component';
    import { boolCompare, isNullOrWhitespace, numCompare } from "@common/Common";
    import { CriteriaVoteEntry, MeetingQuestionReviewReadModel } from "@Evaluation/CriteriaSurveys/Domain/readmodels";

    @Component({
        props: {
            readModel: {
                type: Object,
                required: true
            }
        }
    })
    export default class MeetingQuestionReview extends Vue
    {
        get rm(): MeetingQuestionReviewReadModel { return this.$props.readModel as MeetingQuestionReviewReadModel; }

        ScaleView1: number[] = [9, 8, 7, 6, 5, 4, 3, 2, 1, 1.0 / 2.0, 1.0 / 3.0, 1.0 / 4.0, 1.0 / 5.0, 1.0 / 6.0, 1.0 / 7.0, 1.0 / 8.0, 1.0 / 9.0].sort((a, b) => numCompare(a, b));
        browserActiveTab = 'votes'

        get QuestionId(): string { return this.rm.Id; }
        get ContextName(): string { return this.rm.ContextCriterionName; }
        get FirstItemTitle(): string { return this.rm.FirstCriterionName; }
        get SecondItemTitle(): string { return this.rm.SecondCriterionName; }
        get VotesEntries(): CriteriaVoteEntry[] { return this.rm.VotesEntries?.sort((a, b) => boolCompare(a.SurveyId != null, b.SurveyId != null)) ?? []; }
        get Value(): number | null { return this.rm.Value; }
        get Comment(): string | null { return this.rm.Comment; }

        get EqualitySelected(): boolean { return this.Value != null && this.Value === 1.0; }
        get FirstSelected(): boolean { return this.Value != null && this.Value > 1.0; }
        get SecondSelected(): boolean { return this.Value != null && this.Value < 1.0; }
        get FirstBtnLabel(): string { return this.FirstSelected ? `${GetLabel(this.IntegerValue)} (${this.IntegerValue})` : "More important"; }
        get SecondBtnLabel(): string { return this.SecondSelected ? `${GetLabel(this.IntegerValue)} (${this.IntegerValue})` : "More important"; }
        get IntegerValue(): number | null
        {
            if (!this.Value) return null;
            let value = this.Value < 1 ? 1 / this.Value : this.Value;
            return Math.round(value * 100) / 100;
        }

        get Average(): number 
        {
            const votesEntries = this.VotesEntries.filter(a => a.Value != null && a.VoteStrength > 0);

            if (votesEntries.length < 1)
            {
                return null;
            }

            const sum = votesEntries.map(a => a.VoteStrength)
                .reduce((sum, current) => sum + current, 0);

            let avg = 1.0;

            for (const voteEntry of votesEntries)
            {
                avg *= Math.pow(voteEntry.Value, voteEntry.VoteStrength / sum);
            }

            return avg;
        }

        get Comments(): CriteriaVoteEntry[] 
        {
            return this.VotesEntries.filter(a => isNullOrWhitespace(a.Comment) === false);
        }

        get ScaleView(): number[]
        {
            const average = this.Average;

            const scale = [...this.ScaleView1];

            if (average != null)
            {
                if (scale.some(a => Math.round(100 * a) !== Math.round(100 * average)))
                {
                    scale.push(average);
                }
            }

            return scale.sort((a, b) => numCompare(a, b)).reverse();
        }

        get LeftScaleValuesCount(): number { return this.ScaleView.filter(a => a > 1).length; }

        get RightScaleValuesCount(): number { return this.ScaleView.filter(a => a < 1).length; }

        get AverageDescription(): string
        {
            const avg = this.Average;

            if (avg == null)
            {
                return "N/A";
            }

            if (avg < 1)
            {
                return (1 / avg).toFixedIfNeeded(2);
            }

            return avg.toFixedIfNeeded(2);
        }

        get HistogramValues()
        {
            const voteValues = this.VotesEntries.filter(a => a.Value != null).map(a => a.Value);
            const uniqueValues = new Set([...this.ScaleView1, ...voteValues]);
            const orderedValues = Array.from(uniqueValues).sort((a, b) => b - a);

            let maxCount = 0;

            for (const value of orderedValues)
            {
                const count = voteValues.filter(a => a === value).length;
                maxCount = Math.max(maxCount, count);
            }

            return orderedValues.map(value =>
            {
                const count = voteValues.filter(a => a === value).length;
                const percentOfMax = Math.round(count / maxCount * 100);

                return {
                    Value: value,
                    Count: count,
                    PercentOfMax: percentOfMax
                };
            });
        }

        SaveValueCommand(value: number)
        {
            this.$emit('update-value', value)
        }

        SaveComment(comment: string)
        {
            this.$emit('update-comment', comment)
        }

        ShowContextDetailsCommand()
        {
            this.$emit('show-criterion-details', this.rm.ContextCriterionName, this.rm.ContextCriterionDescription, this.rm.ContextCriterionPath);
        }

        ShowFirstItemDetailsCommand()
        {
             this.$emit('show-criterion-details', this.rm.FirstCriterionName, this.rm.FirstCriterionDescription, this.rm.FirstCriterionPath);
        }

        ShowSecondItemDetailsCommand()
        {
             this.$emit('show-criterion-details', this.rm.SecondCriterionName, this.rm.SecondCriterionDescription, this.rm.SecondCriterionPath);
        }


        FormatValue(value: number): number
        {
            return value < 1 ? 1 / value : value;
        }

        GetColor(value: number): string
        {
            const roundedValue = Math.round(100 * value) / 100;

            if (roundedValue === 1)
            {
                return "bg-color-green2";
            }
            else if (roundedValue > 1)
            {
                return "bg-color-blue2";
            }
            else
            {
                return "bg-color-red2";
            }
        }

        GetCssClass(scaleValue: number, voteValue: number): string | null
        {
            const scaleItemType = GetScaleItemType(scaleValue, voteValue);

            if (scaleItemType === "Empty")
            {
                return "EmptyScaleItem";
            }

            if (scaleItemType === "Filled")
            {
                const colorCssClass = this.GetColor(voteValue);
                return "FilledScaleItem" + " " + colorCssClass;
            }

            if (scaleItemType === "DoubleFilled")
            {
                const colorCssClass = this.GetColor(voteValue);
                return "DoubleFilledScaleItem" + " " + colorCssClass;
            }

            if (scaleItemType === "Hidden")
            {
                return "HiddenScaleItem";
            }

            return null;
        }

        GetScaleItemText(scaleValue: number, voteValue: number): string
        {
            const scaleItemType = GetScaleItemType(scaleValue, voteValue);

            if (scaleItemType !== "DoubleFilled")
            {
                return this.FormatValue(scaleValue).toLocaleString();
            }
            else
            {
                return this.FormatValue(voteValue).toFixedIfNeeded(2);
            }
        }
    }

    function GetLabel(value: number): string
    {
        switch (value)
        {
            case 2.0: return "Weakly";
            case 3.0: return "Moderately";
            case 4.0: return "Moderately +";
            case 5.0: return "Strongly";
            case 6.0: return "Strongly +";
            case 7.0: return "Very Strongly";
            case 8.0: return "Very Strongly +";
            case 9.0: return "Extremely";
            default: return "";
        }
    }

    function GetScaleItemType(scaleValue: number, voteValue: number): string | null
    {
        //no vote
        if (voteValue == null)
        {
            return "Empty";
        }

        //vote on opposite side
        if (scaleValue < 1.0 && voteValue > 1.0)
        {
            return "Empty";
        }

        //vote on opposite side
        if (scaleValue > 1.0 && voteValue < 1.0)
        {
            return "Empty";
        }

        let x = null;

        if (scaleValue <= 1.0 && voteValue <= 1.0)
        {
            x = (1.0 / scaleValue) - (1.0 / voteValue);
        }
        else if (scaleValue >= 1.0 && voteValue >= 1.0)
        {
            x = scaleValue - voteValue;
        }

        x = Math.round(100 * x) / 100;

        if (x === 0.0)
        {
            return "Filled";
        }

        if (x >= 1.0 || x <= -1.0)
        {
            return "Empty";
        }

        if (x > -1.0 && x < 0.0)
        {
            return "Hidden";
        }

        if (x > 0.0 && x < 1.0)
        {
            //alert(x);
            return "DoubleFilled";
        }

        return null;
    }

</script>

<template>
    <div v-if="rm">
        <p class="text-center lead" style="font-size: 1.5rem;">
            In the context of <a class="text-primary" @click="ShowContextDetailsCommand" title="Show details">{{ContextName}}</a>,
            which criterion is more important: <br />
            <a class="text-primary" @click="ShowFirstItemDetailsCommand" title="Show details">{{FirstItemTitle}}</a> or
            <a class="text-primary" @click="ShowSecondItemDetailsCommand" title="Show details">{{SecondItemTitle}}</a>?
        </p>

        <div class="card card-collapsable mt-1">
            <div class="card-header pointer" data-toggle="collapse" href="#cardQuestion">
                Group vote
            </div>
            <div id="cardQuestion" class="card-body collapse show pt-4">
                <div style="min-height: 450px;">
                    <div class="PairwiseComparisonEditor">

                        <div class="row">
                            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12">
                                <div class="mt1 ItemDetailsBox bg-color-blue3">
                                    <h4>{{FirstItemTitle}}</h4>
                                </div>
                                <div class="btn-group ItemButton">
                                    <button type="button" class="btn btn-block dropdown-toggle" data-toggle="dropdown" :class="FirstSelected ? 'btn-success' : 'btn-outline-secondary'">
                                        <i class="fa-regular" aria-hidden="true" :class="FirstSelected ? 'fa-square-check' : 'fa-square'"></i>
                                        {{FirstBtnLabel}}
                                    </button>
                                    <div class="dropdown-menu">
                                        <h4 class="dropdown-header">How much more important?</h4>
                                        <div class="dropdown-divider"></div>
                                        <a class="dropdown-item" @click="SaveValueCommand(2)">Weak (2)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(3)">Moderate (3)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(4)">Moderate + (4)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(5)">Strong (5)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(6)">Strong + (6)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(7)">Very strong (7)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(8)">Very strong + (8)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(9)">Extreme (9)</a>
                                    </div>
                                </div>
                            </div>

                            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12">
                                <div class="mt1 ItemDetailsBox bg-color-red3">
                                    <h4>{{SecondItemTitle}}</h4>
                                </div>
                                <div class="btn-group ItemButton">
                                    <button type="button" class="btn btn-block dropdown-toggle" data-toggle="dropdown" :class="SecondSelected ? 'btn-success' : 'btn-outline-secondary'">
                                        <i class="fa-regular" aria-hidden="true" :class="SecondSelected ? 'fa-square-check' : 'fa-square'"></i> {{SecondBtnLabel}}
                                    </button>
                                    <div class="dropdown-menu">
                                        <h4 class="dropdown-header">How much more important?</h4>
                                        <div class="dropdown-divider"></div>
                                        <a class="dropdown-item" @click="SaveValueCommand(1.0/2.0)">Weak (2)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(1.0/3.0)">Moderate (3)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(1.0/4.0)">Moderate + (4)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(1.0/5.0)">Strong (5)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(1.0/6.0)">Strong + (6)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(1.0/7.0)">Very strong (7)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(1.0/8.0)">Very strong + (8)</a>
                                        <a class="dropdown-item" @click="SaveValueCommand(1.0/9.0)">Extreme (9)</a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-lg-6 offset-lg-3 col-md-6 offset-md-3 col-sm-6 offset-sm-3 col-xs-12">
                                <button type="button" class="btn btn-block btn-equal mt1" @click="SaveValueCommand(1.0)" :class="EqualitySelected === true ? 'btn-success' : 'btn-outline-secondary'">
                                    <i class="fa-regular" aria-hidden="true" :class="EqualitySelected === true ? 'fa-square-check' : 'fa-square'"></i>
                                    <span>Equally important (1)</span>
                                </button>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-lg-6 offset-lg-3 col-md-6 offset-md-3 col-sm-6 offset-sm-3 col-xs-12">
                                <button type="button" class="btn btn-block mt1" @click="SaveValueCommand(null)" 
                                        :class="Value == null ? 'btn-success' : 'btn-outline-secondary'">
                                    <i class="fa-regular" aria-hidden="true" :class="Value == null ? 'fa-square-check' : 'fa-square'"></i>
                                    Follow the average (default)
                                </button>
                            </div>
                        </div>

                        <div style="margin-top: 30px;">
                            <h5 style="margin-bottom: 5px;">Comment:</h5>
                            <textarea rows="5" :value.trim="Comment" @input="SaveComment($event.target.value)" class="form-control" placeholder="Comment..."></textarea>
                        </div>
                    </div>

                </div>
            </div>
        </div>


        <div class="mt-3">
            <div class="mt-4" style="min-height: 500px;">
                <div class="PairwiseComparisonVotesBrowserView">
                    <ul class="nav nav-tabs">
                        <li class="nav-item">
                            <a class="nav-link" @click="browserActiveTab = 'votes'" :class="{'active': browserActiveTab === 'votes'}">Votes</a>
                        </li>
                        <li class="nav-item">
                            <a class="nav-link" @click="browserActiveTab = 'summary'" :class="{'active': browserActiveTab === 'summary'}">Chart</a>
                        </li>
                        <li class="nav-item">
                            <a class="nav-link" @click="browserActiveTab = 'comments'" :class="{'active': browserActiveTab === 'comments'}">Comments</a>
                        </li>
                    </ul>

                    <div class="tab-content">
                        <div v-if="browserActiveTab === 'votes'" class="tab-pane pt-2" :class="{'active': browserActiveTab === 'votes'}">

                            <div class="table-responsive">
                                <table class="table table-striped">
                                    <thead>
                                        <tr>
                                            <th style="border-top: 0;"></th>
                                            <th class="clearfix" style="min-width: 510px; border-top: 0;">
                                                <div class="" style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
                                                    <div class="pull-left" style="flex: 1; text-align: left;">{{FirstItemTitle}}</div>
                                                    <div class="text-center" style="flex: 1; text-align: center;">vs.</div>
                                                    <div class="pull-right" style="flex: 1; text-align: right;">{{SecondItemTitle}}</div>
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr style="text-align: center;">
                                            <td style="text-align: left;">
                                                <strong>Average ({{AverageDescription}})</strong>
                                            </td>
                                            <td style="padding: 0;">
                                                <ul class="clearfix" style="list-style: none; margin: 0; padding: 0;">
                                                    <li v-for="scaleValue in ScaleView1" :class="GetCssClass(scaleValue, Average)">
                                                        {{GetScaleItemText(scaleValue, Average)}}
                                                    </li>
                                                </ul>
                                            </td>
                                        </tr>

                                        <tr style="text-align: center;" v-for="voteEntry in VotesEntries">
                                            <td style="text-align: left;">
                                                {{voteEntry.VotesSourceName ?? '???'}}
                                            </td>
                                            <td style="padding: 0;">
                                                <ul class="clearfix" style="list-style: none; margin: 0; padding: 0;">
                                                    <li v-for="scaleValue in ScaleView1" :class="GetCssClass(scaleValue, voteEntry.Value)">
                                                        {{GetScaleItemText(scaleValue, voteEntry.Value)}}
                                                    </li>
                                                </ul>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                        </div>

                        <div v-if="browserActiveTab === 'summary'" class="tab-pane pt-2" :class="{'active': browserActiveTab === 'summary'}">

                            <div class="table-responsive">
                                <table class="table">
                                    <tbody>
                                        <tr style="text-align: center;" class="bottom noborder">
                                            <td v-for="v in HistogramValues" style="height: 400px; border: none; padding-top: 20px;">
                                                <div v-if="v.Count" v-html="v.Count"></div>
                                                <div :style="{'height': `${v.PercentOfMax}%`}" :class="GetColor(v.Value)" class="bg-color-blue2">&nbsp;</div>
                                            </td>
                                        </tr>
                                        <tr style="text-align: center;">
                                            <td v-for="v in HistogramValues" style="width: 6%;">
                                                {{FormatValue(v.Value).toFixedIfNeeded(2)}}
                                            </td>
                                        </tr>
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <th :colspan="LeftScaleValuesCount" style="text-align: left;">
                                                {{FirstItemTitle}}
                                            </th>
                                            <th style="text-align: center;">vs</th>
                                            <th :colspan="RightScaleValuesCount" style="text-align: right;">
                                                {{SecondItemTitle}}
                                            </th>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                        </div>

                        <div v-if="browserActiveTab === 'comments'" class="tab-pane pt-2" :class="{'active': browserActiveTab === 'comments'}">
                            <blockquote class="blockquote" v-for="c in Comments">
                                <p class="mb-0">{{c.Comment}}</p>
                                <footer class="blockquote-footer">{{c.VotesSourceName ?? '???'}}</footer>
                            </blockquote>
                        </div>
                    </div>
                </div>

            </div>
        </div>

    </div>
</template>

<style scoped>
    .PairwiseComparisonEditor .btn, .PairwiseComparisonEditor textarea {
        border-radius: 0;
    }

    .PairwiseComparisonEditor .mt1 {
        margin-top: 20px !important;
    }

    .PairwiseComparisonEditor .mt2 {
        margin-top: 40px !important;
    }

    .PairwiseComparisonEditor div.ItemDetailsBox {
        border: 1px solid #ddd;
        border-bottom: 0;
        height: 90px;
        max-height: 90px;
        overflow-y: auto;
        padding: 15px 10px;
        background-color: #f5f5f5;
    }

        .PairwiseComparisonEditor div.ItemDetailsBox h4 {
            padding: 0;
            margin: 0;
            text-align: center;
        }

    .PairwiseComparisonEditor div.ItemButton {
        width: 100%;
        padding: 0px;
    }

        .PairwiseComparisonEditor div.ItemButton a.btn {
            height: 38px;
            padding-top: 8px;
        }

        .PairwiseComparisonEditor div.ItemButton div.dropdown-menu {
            width: 100%;
        }

    .PairwiseComparisonEditor a.btn-equal {
        height: 38px;
        padding-top: 8px;
        width: 100%;
    }

    .PairwiseComparisonEditor a.btn-geometric-mean {
        height: 38px;
        padding-top: 8px;
        width: 100%;
    }



    .PairwiseComparisonVotesBrowserView tr.bottom td {
        vertical-align: bottom !important;
    }


    .PairwiseComparisonVotesBrowserView ul li.EmptyScaleItem {
        display: block;
        float: right;
        min-width: 30px;
        width: 5%;
        padding: 8px;
    }

    .PairwiseComparisonVotesBrowserView ul li.FilledScaleItem {
        display: block;
        float: right;
        min-width: 30px;
        width: 5%;
        padding: 8px;
        color: white;
    }

    .PairwiseComparisonVotesBrowserView ul li.HiddenScaleItem {
        display: none;
    }

    .PairwiseComparisonVotesBrowserView ul li.DoubleFilledScaleItem {
        display: block;
        float: right;
        min-width: 60px;
        width: 10%;
        padding: 8px;
        color: white;
    }

</style>